import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Edit2, MoreVertical, Trash2 } from 'react-feather';
import Pagination from '../pagination';
import type { CustomTableProps, TableActions } from './custom-table.props.ts';
import {
  DEFAULT_EMPTY_MESSAGE,
  DEFAULT_LOADING_MESSAGE,
  DEFAULT_TABLE_TITLE,
  DEFAULT_COLUMN_WIDTH,
} from './constants.ts';

function getRowKey<T>(row: T, rowKey: keyof T | ((item: T) => string | number)): string | number {
  return typeof rowKey === 'function' ? rowKey(row) : (row[rowKey] as string | number);
}

function SelectAllCheckbox({
  allSelected,
  someSelected,
  onSelectAll,
}: {
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: (checked: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.indeterminate = someSelected && !allSelected;
    }
  }, [allSelected, someSelected]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      className="border-[#101828] border-[2px] w-[15px] h-[15px] cursor-pointer ml-2  "
      checked={allSelected}
      onChange={(e) => onSelectAll(e.target.checked)}
      aria-label="Select all rows"
    />
  );
}

const ACTIONS_MENU_MIN_WIDTH = 120;

function ActionsCell<T>({ row, actions }: { row: T; actions: TableActions<T> }) {
  const { onView, onEdit, onDelete, canEdit, canDelete, readOnly, viewActionLabel, ActionIcon } =
    actions;
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const portalIdRef = useRef(`custom-table-actions-${Math.random().toString(36).slice(2)}`).current;

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setDropdownPosition(null);
  }, []);

  useLayoutEffect(() => {
    if (menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right - ACTIONS_MENU_MIN_WIDTH,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return () => {};
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inButton = buttonRef.current?.contains(target);
      const inPortal = document.getElementById(portalIdRef)?.contains(target);
      if (!inButton && !inPortal) closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, closeMenu, portalIdRef]);

  useEffect(() => {
    if (!menuOpen || !buttonRef.current) return () => {};
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.right - ACTIONS_MENU_MIN_WIDTH,
        });
      }
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [menuOpen]);

  const align = readOnly ? 'text-center' : 'text-right';
  const justify = readOnly ? 'justify-center' : 'justify-end';
  const showEdit = !readOnly && onEdit && (canEdit === undefined || canEdit(row));
  const showDelete = !readOnly && onDelete && (canDelete === undefined || canDelete(row));
  const hasActions = onView || showEdit || showDelete;
  if (!hasActions) {
    return (
      <td className={`py-3 px-3 ${align}`}>
        <div className={`flex items-center ${justify}`}>&mdash;</div>
      </td>
    );
  }

  const menuContent = dropdownPosition && (
    <div
      id={portalIdRef}
      className="min-w-[120px] py-1 rounded-lg border border-gray-200 bg-white shadow-lg"
      role="menu"
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        zIndex: 99999,
      }}
    >
      {onView && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onView(row);
            closeMenu();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-[#0A8BC7] hover:bg-[#0A8BC7]/10"
        >
          {ActionIcon ?? <Eye className="w-4 h-4 shrink-0" />}
          <span>{viewActionLabel ?? 'View'}</span>
        </button>
      )}
      {showEdit && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onEdit(row);
            closeMenu();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
        >
          <Edit2 className="w-4 h-4 shrink-0" />
          <span>Edit</span>
        </button>
      )}
      {showDelete && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onDelete(row);
            closeMenu();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 shrink-0" />
          <span>Delete</span>
        </button>
      )}
    </div>
  );

  return (
    <td className={`py-3 px-3 ${align}`}>
      <div ref={menuRef} className="relative flex items-center justify-end">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="py-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Open actions menu"
          aria-expanded={menuOpen}
        >
          <MoreVertical className="w-5 h-5" />
        </button>
        {menuOpen && menuContent && createPortal(menuContent, document.body)}
      </div>
    </td>
  );
}

function CustomTable<T>({
  columns,
  data,
  rowKey,
  loading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage: rowsPerPageProp,
  title = DEFAULT_TABLE_TITLE,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  loadingMessage = DEFAULT_LOADING_MESSAGE,
  actions,
  selectionConfig,
  headerLeftContent,
  headerRightContent,
}: CustomTableProps<T>) {
  const colSpan = columns.length + (actions ? 1 : 0);
  const rowsPerPage = rowsPerPageProp ?? (totalPages > 0 ? Math.ceil(totalCount / totalPages) : 10);

  const selectAllState =
    selectionConfig && data.length > 0
      ? (() => {
          const ids = data.map((row) => selectionConfig.getRowId(row));
          const selectedCount = ids.filter((id) => selectionConfig.selectedIds.includes(id)).length;
          const allSelected = selectedCount === ids.length;
          const someSelected = selectedCount > 0;
          return { allSelected, someSelected, onSelectAll: selectionConfig.onSelectAll };
        })()
      : null;

  const alignClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  const justifyClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'justify-center';
      case 'right':
        return 'justify-end';
      default:
        return 'justify-start';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm mb-4">
      <div className="py-3 px-3 border-b border-gray-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {headerLeftContent}
          <h2 className="text-lg font-bold text-gray-900">
            {title} ({totalCount})
          </h2>
        </div>
        {headerRightContent}
      </div>
      <div className="overflow-hidden">
        <table className="w-full table-fixed">
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} className={col.width ?? DEFAULT_COLUMN_WIDTH} />
            ))}
            {actions && <col className="w-[10%]" />}
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200 bg-gray-100">
              {columns.map((col, colIndex) => (
                <th
                  key={col.key}
                  className={`py-2 px-2 text-sm font-semibold ${alignClass(col.align)}`}
                >
                  <span className={`flex items-center gap-3 ${justifyClass(col.align)}`}>
                    {selectionConfig && colIndex === 0 && (
                      <SelectAllCheckbox
                        allSelected={selectAllState?.allSelected ?? false}
                        someSelected={selectAllState?.someSelected ?? false}
                        onSelectAll={selectAllState?.onSelectAll ?? (() => {})}
                      />
                    )}
                    {col.headerIcon && <span className="flex-shrink-0">{col.headerIcon}</span>}
                    {col.header}
                  </span>
                </th>
              ))}
              {actions && (
                <th className="text-end py-2 px-3 text-sm font-semibold" title="Actions">
                  <span className="flex justify-end" aria-label="Actions">
                    <MoreVertical className="w-5 h-5 text-gray-500" />
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="py-12 text-center text-gray-500 text-sm">
                  {loadingMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={String(getRowKey(row, rowKey))}
                  className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`py-2 overflow-hidden text-gray-600 ${alignClass(col.align)} ${col.cellClassName ?? 'px-2'}`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                  {actions && <ActionsCell row={row} actions={actions} />}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && data.length === 0 && (
        <div className="text-center py-12 text-gray-500 text-sm">{emptyMessage}</div>
      )}
      {!loading && data.length > 0 && totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100">
          <Pagination
            key={currentPage}
            activePage={currentPage}
            rowsPerPage={rowsPerPage}
            totalItems={totalCount}
            setOffset={(offset) => onPageChange(Math.floor(offset / rowsPerPage) + 1)}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}

export default CustomTable;
export type {
  CustomTableProps,
  TableActions,
  ColumnDef,
  SelectionConfig,
} from './custom-table.props.ts';
